// "Coach sees what you open": while a voice call is live, opening a goal, a
// task or habit sheet or a habit's stats sends the coach a short [Viewing] note
// over the realtime session. Shared by web and native.

import { type QuitStats, quitText } from './quitHabit'
import { scheduleText } from './schedule'

export const VIEWING_PREFIX = '[Viewing]'
export const VIEWING_MAX_CHARS = 600
export const VIEWING_INSTRUCTIONS = `\nMessages starting with "${VIEWING_PREFIX}" are not spoken by them: the app sends one when they open something on screen during the call (a goal, a task, a habit). When asked to respond to one, acknowledge it in one short, natural sentence (like "Ah, your meditation habit, a 12-day streak!") and maybe one quick question, then carry on. Never read them out verbatim and never mention the brackets. If they are mid-topic, keep the note in mind and use it when it helps. A note for a screen you just opened with show_in_app is only context: you already introduced it, so never greet it again.`

export type Viewing = { key: string; text: string }

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s)
const clean = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim()

function finish(parts: (string | false | null | undefined)[]): string {
  const text = [VIEWING_PREFIX, ...parts.filter(Boolean)].join(' ')
  return clip(text, VIEWING_MAX_CHARS)
}

export type GoalViewingInput = {
  title: string
  progress: number
  milestone?: string | null
  target?: string | null
  nextTasks: string[]
  done?: boolean
}

export function goalViewing(id: string, g: GoalViewingInput): Viewing {
  const next = g.nextTasks.map(clean).filter(Boolean).slice(0, 3)
  return {
    key: `goal:${id}`,
    text: finish([
      `Goal "${clip(clean(g.title), 120)}", ${Math.round(g.progress)}% done.`,
      g.done && 'Every task is done.',
      g.milestone && `Current milestone: "${clip(clean(g.milestone), 80)}".`,
      g.target && `Current target: "${clip(clean(g.target), 80)}".`,
      next.length > 0 && `Next tasks: ${next.map((t) => `"${clip(t, 80)}"`).join(', ')}.`,
    ]),
  }
}

export type HabitViewingInput = {
  title: string
  period?: string | null
  recursDays?: readonly number[] | null
  streak?: number | null
  strength?: number | null
  doneToday?: boolean | null
  quit?: QuitStats | null
}

export function habitViewing(id: string, h: HabitViewingInput, page: 'sheet' | 'stats'): Viewing {
  return {
    key: `habit:${id}:${page}`,
    text: finish([
      `${page === 'stats' ? 'Habit stats for' : 'Habit'} "${clip(clean(h.title), 120)}" (${scheduleText(h.period, h.recursDays)}).`,
      h.quit
        ? `Quit habit: ${quitText(h.quit)}.`
        : h.streak != null && `Streak: ${h.streak} ${h.streak === 1 ? 'day' : 'days'}.`,
      h.strength != null && `Habit strength: ${Math.round(h.strength)}/100.`,
      h.doneToday != null && (h.doneToday ? 'Done today.' : 'Not done yet today.'),
    ]),
  }
}

export type TaskViewingInput = {
  title: string
  note?: string | null
  dueDate?: number | null
  status: 'backlog' | 'in-progress' | 'done'
  goalTitle?: string | null
}

export function taskViewing(id: string, t: TaskViewingInput, now = Date.now()): Viewing {
  const note = clean(t.note)
  return {
    key: `task:${id}`,
    text: finish([
      `Task "${clip(clean(t.title), 120)}"${t.goalTitle ? ` in goal "${clip(clean(t.goalTitle), 80)}"` : ''}.`,
      `Status: ${t.status === 'in-progress' ? 'in progress' : t.status === 'done' ? 'done' : 'not started'}.`,
      t.dueDate != null && `Due ${dueText(t.dueDate, now)}.`,
      note && `Note: "${clip(note, 220)}".`,
    ]),
  }
}

function dueText(due: number, now: number): string {
  const day = (ms: number) =>
    Math.floor((ms - new Date(ms).getTimezoneOffset() * 60_000) / 86_400_000)
  const diff = day(due) - day(now)
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff === -1) return 'yesterday (overdue)'
  if (diff < 0) return `${-diff} days ago (overdue)`
  if (diff < 7) return `in ${diff} days`
  return `on ${new Date(due).toISOString().slice(0, 10)}`
}

export type ViewingCallState = {
  // The coach is talking, about to talk, or running a tool.
  coachBusy: boolean
  userTalking: boolean
  // When the user last started or stopped speaking (ms), 0 if never.
  lastUserSpeechAt: number
  // A focus session is on: add context silently, never prompt a reply.
  quiet?: boolean
}

export const VIEWING_DEBOUNCE_MS = 1500
export const VIEWING_MIN_GAP_MS = 10_000
export const VIEWING_USER_QUIET_MS = 2000
// A screen the coach itself just opened (show_in_app) is shared without a reply.
export const VIEWING_SHOWN_QUIET_MS = 10_000

/**
 * Decides when a [Viewing] note goes out: the item must stay open for the
 * debounce, not have been sent already this call, wait out the rate limit, and never
 * cut in while the coach is talking. It asks for a reply only when the user is
 * silent; otherwise the note is added quietly for the coach to use.
 */
export class ViewingGate {
  private pending: (Viewing & { since: number }) | null = null
  private sentKeys = new Set<string>()
  private lastSentAt = Number.NEGATIVE_INFINITY
  private shownQuietUntil = Number.NEGATIVE_INFINITY

  constructor(
    private readonly debounceMs = VIEWING_DEBOUNCE_MS,
    private readonly minGapMs = VIEWING_MIN_GAP_MS,
    private readonly userQuietMs = VIEWING_USER_QUIET_MS,
  ) {}

  /** The item now on screen (null when nothing reportable is open). */
  offer(v: Viewing | null, now: number) {
    if (!v) {
      this.pending = null
      return
    }
    if (this.pending?.key === v.key) {
      this.pending = { ...v, since: this.pending.since }
      return
    }
    this.pending = this.sentKeys.has(v.key) ? null : { ...v, since: now }
  }

  /** The coach opened a screen at `at`; what opens next is its own doing. */
  coachShowed(at: number) {
    this.shownQuietUntil = Math.max(this.shownQuietUntil, at + VIEWING_SHOWN_QUIET_MS)
  }

  /** What to send now, if anything. Call it on a short timer. */
  poll(now: number, call: ViewingCallState): { text: string; respond: boolean } | null {
    const p = this.pending
    if (!p) return null
    if (now - p.since < this.debounceMs) return null
    if (now - this.lastSentAt < this.minGapMs) return null
    if (call.coachBusy) return null
    const respond =
      p.since > this.shownQuietUntil &&
      !call.quiet &&
      !call.userTalking &&
      now - call.lastUserSpeechAt >= this.userQuietMs
    return { text: p.text, respond }
  }

  /** The note from the last poll went out. */
  sent(now: number) {
    if (!this.pending) return
    this.sentKeys.add(this.pending.key)
    this.lastSentAt = now
    this.pending = null
  }

  reset() {
    this.pending = null
    this.sentKeys.clear()
    this.lastSentAt = Number.NEGATIVE_INFINITY
    this.shownQuietUntil = Number.NEGATIVE_INFINITY
  }
}

type TreeTask = { title: string; completedAt: number | null }
export type GoalTreeLike = {
  title: string
  milestones: { title: string; targets: { title: string; tasks: TreeTask[] }[] }[]
}

/** A goal page's note, from the goal tree the page already has. */
export function goalTreeViewing(id: string, tree: GoalTreeLike): Viewing {
  const open = (t: TreeTask) => t.completedAt === null
  const tasks = tree.milestones.flatMap((m) => m.targets.flatMap((tg) => tg.tasks))
  const done = tasks.length - tasks.filter(open).length
  const milestone = tree.milestones.find((m) => m.targets.some((tg) => tg.tasks.some(open)))
  const target = milestone?.targets.find((tg) => tg.tasks.some(open))
  return goalViewing(id, {
    title: tree.title,
    progress: tasks.length ? (done / tasks.length) * 100 : 0,
    milestone: milestone?.title,
    target: target?.title,
    nextTasks: tasks.filter(open).map((t) => t.title),
    done: tasks.length > 0 && done === tasks.length,
  })
}

/** A habit stats page's note, from getHabitStats. */
export function habitStatsViewing(stats: {
  habit: { id: string; title: string; period: string; recursDays: number[] | null }
  streak: number
  strength: number
  year: string
  quit?: QuitStats | null
}): Viewing {
  return habitViewing(
    stats.habit.id,
    {
      title: stats.habit.title,
      period: stats.habit.period,
      recursDays: stats.habit.recursDays,
      streak: stats.streak,
      strength: stats.strength,
      doneToday: stats.year.endsWith('d'),
      quit: stats.quit,
    },
    'stats',
  )
}
